// FinalMocks2026 — private prep page for 2026 Finalists (/2026-final-mocks)
// Unlisted: routed but intentionally not linked from the navbar.
import { useEffect } from 'react';
import * as React from 'react';

const BLUE = '#004d99';
const BLUE2 = '#0066cc';
const INK = '#1a1a2e';
const BODY = '#42475a';

// Tiny inline-markdown renderer: **bold**, *italic*, and "smart" quotes.
function fmt(text) {
  const parts = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) parts.push(<strong key={key++}>{m[1]}</strong>);
    else parts.push(<em key={key++}>{m[2]}</em>);
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

const FRAMING = [
  { n: 1, h: 'What kind of evidence is this, really?', s: 'design, what it can and cannot license' },
  { n: 2, h: 'What is the statistic actually saying?', s: 'effect size vs significance, baselines, multiplicity' },
  { n: 3, h: 'Who and what does this apply to?', s: 'generalisability, the gap between the result and the claim' },
  { n: 4, h: 'What else could explain this, and how would you find out?', s: 'confounds, alternatives, follow-up design' },
];

const PAPERS = [
  {
    tag: 'Paper A',
    title: 'Semaglutide and cardiovascular outcomes in obesity (SELECT trial)',
    cite: 'Lincoff AM, Brown-Frandsen K, Colhoun HM, et al. (2023). "Semaglutide and Cardiovascular Outcomes in Obesity without Diabetes." New England Journal of Medicine 389(24): 2221–2232.',
    doi: '10.1056/NEJMoa2307563',
    questions: [
      { id: 'A1', q: 'The trial reports a "20% reduction in risk." A 20% reduction *of what*, and how big is the benefit to an actual patient?', hint: 'Work out for yourself the difference between the *relative* effect the headline quotes and the *absolute* effect a patient experiences over the trial’s follow-up. Then ask which number matters for which decision — a regulator, a clinician, a health system, and a patient each care about a different one.' },
      { id: 'A2', q: 'The primary endpoint is a three-part composite. Is the benefit shared evenly across the three components, and why does that matter?', hint: 'Look at the hazard ratios for the individual components separately from the composite. Ask whether a single component is doing most of the work, and what it would mean for the headline claim if it were.' },
      { id: 'A3', q: 'The trial enrolled a specific population. Draw the boundary around who these results actually apply to.', hint: 'Catalogue the enrolment criteria and the demographics — who was *in*, who was *excluded*, and what that does to the reach of the conclusion. Then separate the legitimate generalisability cautions from the cheap shots.' },
      { id: 'A4', q: 'The authors argue the benefit is "partly weight-independent." What would it actually take to prove that — and did they?', hint: 'The trial shows weight fell and cardiovascular events fell. The claim that some of the benefit runs through pathways *other than* weight loss is a separate, harder claim. Ask what analysis would isolate it, and whether the trial as run can support the inference or only gesture at it.' },
    ],
  },
  {
    tag: 'Paper B',
    title: 'Individually specific name-like calls in African elephants',
    cite: 'Pardo MA, Fristrup K, Lolchuragi DS, et al. (2024). "African elephants address one another with individually specific name-like calls." Nature Ecology & Evolution 8: 1353–1364.',
    doi: '10.1038/s41559-024-02420-w',
    questions: [
      { id: 'B1', q: 'The model identifies the receiver only ~27% of the time. Is that a weak result or a strong one — and against what baseline?', hint: 'Resist the instinct to judge an accuracy number on its face. Find what the authors compared it to (the permuted/chance baseline) and reason about the *gap*, not the absolute value. Ask how many standard deviations above chance the result sits.' },
      { id: 'B2', q: 'The authors run a second model that deliberately *fails*. Why would a researcher build a test designed to come back negative?', hint: 'Compare the two cross-validation schemes — the headline one and the cross-caller one — and work out what each can and cannot prove. Ask why the contrast between them is more informative than either alone.' },
      { id: 'B3', q: 'The playback experiment has only 17 subjects. What does it add that the classifier — run on hundreds of calls — cannot?', hint: 'Ask what kind of claim a statistical classifier establishes versus what a behavioural experiment establishes. The two are doing genuinely different jobs.' },
      { id: 'B4', q: 'Suppose the model is reading something other than a "name." Construct the most dangerous alternative explanation and then defeat it.', hint: 'The classifier could be picking up receiver identity without any "addressing" going on at all — for example if a caller reliably changes *context* when addressing particular individuals. Build that confound concretely, then design the analysis that would tell the two apart.' },
    ],
  },
  {
    tag: 'Paper C',
    title: 'Gut microbiome predicts personalised fibre response in prediabetes',
    cite: 'Song D, Feng G, Ma Y, et al. (2025). "Gut microbiome predicts personalized responses to dietary fiber in prediabetes: a randomized, open-label trial." Nature Communications 16: 66498.',
    doi: '10.1038/s41467-025-66498-x',
    questions: [
      { id: 'C1', q: 'The primary endpoint was null. Before reading another line, what is the *most* you are now allowed to conclude?', hint: 'Pin down what a pre-specified primary endpoint is *for*, and what a null result on it does and does not establish. Then notice how the rest of the paper is built on top of that null — and ask whether the foundation holds the building.' },
      { id: 'C2', q: 'The positive findings live in post-hoc subgroups (clusters). What changes about a result the moment it is found *after* looking at the data?', hint: 'Trace how the four clusters were defined and when. Ask what multiplicity does to a "significant" subgroup, and what the *correct* statistical test for "the effect differs across groups" actually is.' },
      { id: 'C3', q: 'The responder-prediction model reports an AUC of 0.81. Two traps are hiding in that number — find both.', hint: 'First, interrogate what AUC actually measures (and what the tempting "correct 81% of the time" reading gets wrong). Second, and more dangerous, ask where the *labels* the model predicts came from.' },
      { id: 'C4', q: 'The paper’s "independent validation" is not in the trial’s target population. Why does that gap matter, and what study would close it?', hint: 'Examine where the validation cohorts came from (the population, the disease stage, the research group) and compare that to the population the predictor is *meant* to serve. Then design the confirmatory study that would actually license the clinical claim.' },
    ],
  },
  {
    tag: 'Paper D',
    title: 'A 6000-year genomic transect of the Bogotá Altiplano',
    cite: 'Casas-Vargas A, Mas-Sandoval A, et al. (2025). "A 6000-year-long genomic transect from the Bogotá Altiplano reveals multiple genetic shifts in the demographic history of Colombia." Science Advances 11(22): eads6284.',
    doi: '10.1126/sciadv.ads6284',
    questions: [
      { id: 'D1', q: 'The claim is *complete* genetic replacement. What feature of the sampling makes that claim hard to defend — and what weaker claim does the data actually support?', hint: 'Map the samples onto the timeline: how many individuals, from how many sites, at which dates — and where the gaps are. Ask what an unobserved interval does to a claim about *how* a transition happened.' },
      { id: 'D2', q: 'The paper leans on f4-statistics, runs-of-homozygosity, and contamination estimates. You will not have memorised these — so how do you reason about a method you half-know?', hint: 'Pick one unfamiliar tool (say, ROH as a window onto effective population size, or f4 ≈ 0 as a symmetry test) and work out the *direction* of its logic: which way does the quantity move when the underlying reality moves?' },
      { id: 'D3', q: 'Contamination is the ancient-DNA equivalent of a confound. Which way would it bias the result, and is the authors’ authentication enough to rule it out?', hint: 'Reason about what present-day contaminating DNA would do to an ancient sample’s apparent ancestry, and then ask whether the reported authentication checks are *adequate to bound* that specific risk.' },
      { id: 'D4', q: 'The genetic shift lines up with a pottery transition. Does the correlation prove the people and the pots arrived together?', hint: 'Examine the archaeological correlation the authors invoke and ask the classic question: what does co-occurrence establish, and what does it merely suggest? Then ask what *independent* evidence would strengthen or break the coupled-migration story.' },
    ],
  },
];

export default function FinalMocks2026() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ background: '#f6f8fb' }}>
      <style>{`
        .fm-wrap { max-width: 880px; margin: 0 auto; padding: 0 22px 90px; }
        .fm-q { transition: box-shadow .18s ease, transform .18s ease; }
        .fm-q:hover { box-shadow: 0 8px 26px rgba(0,77,153,0.13); transform: translateY(-2px); }
        .fm-framing-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 640px) { .fm-framing-grid { grid-template-columns: 1fr; } }
      `}</style>

      {/* Hero */}
      <div style={{
        background: `linear-gradient(135deg, ${BLUE} 0%, #013366 100%)`,
        color: '#fff', padding: '64px 22px 56px', textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 999, padding: '5px 16px', fontSize: 12, letterSpacing: 2,
          textTransform: 'uppercase', marginBottom: 22, fontWeight: 600,
          color: 'rgba(255,255,255,0.92)',
        }}>
          Private · For 2026 Finalists
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 700, margin: '0 0 14px', lineHeight: 1.15 }}>
          2026 Final Round
        </h1>
        <p style={{ fontSize: 20, fontWeight: 500, margin: '0 0 8px', color: 'rgba(255,255,255,0.95)' }}>
          Mock Preparation — Guiding Questions
        </p>
        <p style={{ maxWidth: 600, margin: '0 auto', fontSize: 15.5, lineHeight: 1.6, color: 'rgba(255,255,255,0.82)' }}>
          This is not a mock exam. It trains the way you read a paper — the reflexes the
          final round rewards. Work through each question with the paper open.
        </p>
      </div>

      <div className="fm-wrap">
        {/* Intro / how to use */}
        <div style={{
          background: '#fff', borderRadius: 16, padding: '30px 30px 26px',
          marginTop: -34, boxShadow: '0 6px 30px rgba(16,42,77,0.10)',
          border: '1px solid #e7edf5',
        }}>
          <p style={{ color: BODY, fontSize: 15.5, lineHeight: 1.7, marginTop: 0 }}>
            The final-round written exam will hand you four research papers you have never seen
            and ask you to reason about them under time pressure. You cannot prepare by memorising
            facts. You prepare by building reflexes — the habit of asking the right question of any
            paper, in any field, within the first few minutes of reading it.
          </p>
          <p style={{ color: BODY, fontSize: 15.5, lineHeight: 1.7 }}>
            Below are <strong>four guiding questions for each of four practice papers</strong> (16
            in total). They are deliberately <em>larger</em> than exam questions: each opens onto a
            whole family of exam-style problems rather than a single answer. The four practice
            papers are different from the four finals papers — the skills are identical.
          </p>
          <p style={{ color: INK, fontWeight: 600, fontSize: 15.5, margin: '22px 0 14px' }}>
            As you prepare, think about:
          </p>
          <div className="fm-framing-grid">
            {FRAMING.map((f) => (
              <div key={f.n} style={{
                background: '#f3f7fc', borderRadius: 12, padding: '16px 18px',
                borderLeft: `3px solid ${BLUE2}`,
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span style={{
                    flexShrink: 0, width: 26, height: 26, borderRadius: 999, background: BLUE,
                    color: '#fff', fontSize: 14, fontWeight: 700, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', marginTop: 1,
                  }}>{f.n}</span>
                  <div>
                    <div style={{ color: INK, fontWeight: 600, fontSize: 14.5, lineHeight: 1.4 }}>{f.h}</div>
                    <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.45 }}>{f.s}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: BODY, fontSize: 14.5, lineHeight: 1.65, margin: '20px 0 0' }}>
            Every guiding question below is an instance of one of these four. Once you see that, you
            can generate your own guiding questions for any paper the finals throw at you.
          </p>
        </div>

        {/* Papers */}
        {PAPERS.map((p) => (
          <section key={p.tag} style={{ marginTop: 46 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={{
                background: BLUE, color: '#fff', fontSize: 12.5, fontWeight: 700,
                letterSpacing: 1.5, textTransform: 'uppercase', padding: '5px 12px',
                borderRadius: 7,
              }}>{p.tag}</span>
            </div>
            <h2 style={{ color: INK, fontSize: 23, fontWeight: 700, margin: '10px 0 8px', lineHeight: 1.25 }}>
              {p.title}
            </h2>
            <p style={{ color: '#6b7280', fontSize: 13.5, fontStyle: 'italic', lineHeight: 1.55, margin: '0 0 8px' }}>
              {p.cite}
            </p>
            {p.doi && (
              <a
                href={`https://doi.org/${p.doi}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  color: BLUE2, fontSize: 13.5, fontWeight: 600, textDecoration: 'none',
                  border: `1px solid ${BLUE2}`, borderRadius: 8, padding: '6px 13px',
                }}
              >
                Read the paper
                <span style={{ fontSize: 12, opacity: 0.85 }}>doi.org/{p.doi} ↗</span>
              </a>
            )}

            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
              {p.questions.map((qq) => (
                <div key={qq.id} className="fm-q" style={{
                  background: '#fff', borderRadius: 14, padding: '20px 22px',
                  border: '1px solid #e7edf5', boxShadow: '0 2px 10px rgba(16,42,77,0.05)',
                }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <span style={{
                      flexShrink: 0, background: '#eaf1fb', color: BLUE, fontWeight: 700,
                      fontSize: 13.5, padding: '4px 10px', borderRadius: 7, letterSpacing: 0.5,
                      marginTop: 2,
                    }}>{qq.id}</span>
                    <p style={{ color: INK, fontSize: 16.5, fontWeight: 600, lineHeight: 1.5, margin: 0 }}>
                      {fmt(qq.q)}
                    </p>
                  </div>
                  <div style={{
                    marginTop: 14, paddingTop: 14, borderTop: '1px dashed #dde6f1',
                    display: 'flex', gap: 10, alignItems: 'baseline',
                  }}>
                    <span style={{
                      flexShrink: 0, color: BLUE2, fontWeight: 700, fontSize: 12,
                      letterSpacing: 1, textTransform: 'uppercase',
                    }}>Hint</span>
                    <p style={{ color: BODY, fontSize: 14.5, lineHeight: 1.65, margin: 0 }}>
                      {fmt(qq.hint)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}

        <p style={{
          textAlign: 'center', color: '#9099a8', fontSize: 13, marginTop: 60,
          borderTop: '1px solid #e1e8f0', paddingTop: 26,
        }}>
          International Research Olympiad · 2026 Final Round · Confidential to invited finalists
        </p>
      </div>
    </div>
  );
}
